/**
 * youthLearner.jsx — thin orchestrator (was 1 985 lines, now ~80)
 * All game logic, screens, and state live in youth/ sub-modules.
 */
import React, { useState } from 'react';
import { useUI } from './shared';
import { COURSE_BY_ID } from './youthData';
import {
  useYouthState, readFx, writeFx,
  playTone, buzz, COURSE_SOUND_THEMES, fx,
  YouthContext, XPFloaterHost,
} from './youth/youthState';
import { YouthHome } from './youth/YouthHome';
import {
  YouthSettings, BadgeGallery, LockedPreview,
} from './youth/youthSideScreens';
import {
  CourseIntro, NodeRunner, BadgeUnlock, LevelCelebration,
} from './youth/youthCourseNodes';

/* ============================================================
   YouthLearner — root component (router + context provider)
   ============================================================ */
const YouthLearner = () => {
  const [state, update] = useYouthState();
  const { showToast } = useUI();
  const [view, setView] = useState({ screen: 'home' });
  const [showBadge, setShowBadge] = useState(null);
  const [showCelebration, setShowCelebration] = useState(null);
  const [fxSettings, setFxSettings] = useState(readFx);
  const [nodeKey, setNodeKey] = useState(0);

  const toggleFx = () => {
    const isOn = fxSettings.sound || fxSettings.haptic;
    const next = { sound: !isOn, haptic: !isOn };
    setFxSettings(next);
    writeFx(next);
    showToast(next.sound ? 'Sound & haptics ON' : 'Sound & haptics OFF', 'info');
    if (next.sound) {
      const cid = view.screen === 'node' || view.screen === 'intro' ? view.courseId : null;
      const t = COURSE_SOUND_THEMES[cid] || COURSE_SOUND_THEMES._default;
      playTone(t.correct, 140);
      setTimeout(() => playTone(t.badge[1], 140), 160);
    }
  };

  const setFxSetting = (key, value) => {
    const next = { ...fxSettings, [key]: value };
    setFxSettings(next);
    writeFx(next);
  };

  const previewCourseTheme = (courseId) => {
    const t = COURSE_SOUND_THEMES[courseId] || COURSE_SOUND_THEMES._default;
    if (fxSettings.sound) {
      playTone(t.correct, 140);
      setTimeout(() => playTone(t.badge[1], 140), 160);
      setTimeout(() => playTone(t.badge[2], 220), 340);
    }
    if (fxSettings.haptic) buzz([40, 40, 40]);
  };

  const restartCurrentNode = () => {
    setNodeKey((k) => k + 1);
    showToast('Lesson restarted', 'info');
  };

  const goHome = () => setView({ screen: 'home' });
  const goToCourse = (courseId) => setView({ screen: 'intro', courseId });
  const goToLocked = (id) => setView({ screen: 'locked', id });
  const goToBadges = () => setView({ screen: 'badges' });
  const goToSettings = () => setView({ screen: 'settings' });
  const startCourse = (courseId) => setView({ screen: 'node', courseId, nodeIdx: 0 });

  const handleNodeDone = (course, nodeIdx) => {
    const key = `${course.id}:${course.nodes[nodeIdx].id}`;
    update((s) => {
      const cs = s.courses || {};
      const cur = cs[course.id] || { nodesDone: [] };
      const nodesDone = cur.nodesDone.includes(course.nodes[nodeIdx].id) ? cur.nodesDone : [...cur.nodesDone, course.nodes[nodeIdx].id];
      const finished = nodesDone.length >= course.nodes.length;
      const newDailyDone = Math.min(3, (s.dailyDone || 0) + 1);
      const daily = { dailyDone: newDailyDone };
      if (newDailyDone === 3 && s.dailyDone < 3) {
        setTimeout(() => showToast('Goal complete today! ⚡ +50 XP bonus', 'success'), 200);
        daily.xp = (s.xp || 0) + 50;
      }
      return {
        ...s, ...daily,
        xp: daily.xp ?? s.xp,
        courses: { ...cs, [course.id]: { ...cur, nodesDone } },
        completedCourses: finished ? { ...(s.completedCourses || {}), [course.id]: true } : s.completedCourses,
        earnedBadges: finished ? { ...(s.earnedBadges || {}), [course.id]: new Date().toISOString().slice(0, 10) } : s.earnedBadges,
      };
    });
    const nextIdx = nodeIdx + 1;
    if (nextIdx < course.nodes.length) {
      setView({ screen: 'node', courseId: course.id, nodeIdx: nextIdx });
    } else {
      update((s) => ({ ...s, xp: (s.xp || 0) + 100 }));
      fx.badge(fxSettings, course.id);
      setShowBadge(course);
    }
  };

  const finishBadgeToCelebration = () => {
    const course = showBadge;
    setShowBadge(null);
    setShowCelebration(course);
  };

  const closeCelebration = () => {
    setShowCelebration(null);
    goHome();
  };

  return (
    <YouthContext.Provider value={{ state, update, fxSettings, toggleFx, setFxSetting, previewCourseTheme, restartCurrentNode }}>
      <XPFloaterHost>
        {view.screen === 'home' && (
          <YouthHome state={state} update={update}
            goToCourse={goToCourse} goToLocked={goToLocked}
            goToBadges={goToBadges} goToSettings={goToSettings} />
        )}
        {view.screen === 'settings' && <YouthSettings back={goHome} goToCourse={goToCourse} goToLocked={goToLocked} />}
        {view.screen === 'badges' && <BadgeGallery state={state} back={goHome} />}
        {view.screen === 'locked' && <LockedPreview courseId={view.id} state={state} back={goHome} goToCourse={goToCourse} />}
        {view.screen === 'intro' && (() => {
          const course = COURSE_BY_ID[view.courseId];
          return <CourseIntro course={course} back={goHome} start={() => startCourse(view.courseId)} />;
        })()}
        {view.screen === 'node' && (() => {
          const course = COURSE_BY_ID[view.courseId];
          return (
            <NodeRunner
              key={`${view.courseId}-${view.nodeIdx}-${nodeKey}`}
              course={course}
              nodeIdx={view.nodeIdx}
              onNodeDone={() => handleNodeDone(course, view.nodeIdx)}
              back={() => setView({ screen: 'intro', courseId: view.courseId })}
            />
          );
        })()}
        {showBadge && <BadgeUnlock course={showBadge} onContinue={finishBadgeToCelebration} />}
        {showCelebration && (
          <LevelCelebration
            course={showCelebration}
            onShare={() => showToast('Certificate saved!', 'success')}
            onContinue={closeCelebration}
          />
        )}
      </XPFloaterHost>
    </YouthContext.Provider>
  );
};

export default YouthLearner;
